import React from 'react';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from 'gatsby';
import { CityStatistics } from '../../pages/index';
import { withTrailingSlash } from '../../utils/canonicalUrl';
import { pharmacyAppUrl } from '../../utils/pharmacyUrl';

const toSlug = (city: string) =>
  city
    .toLowerCase()
    .replace(/ä/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/å/g, 'a')
    .replace(/\s+/g, '-');

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(12),
  },
  intro: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(5),
  },
  title: {
    fontSize: '36px',
    fontWeight: 'bold',
  },
  cityDetails: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:first-of-type': {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    '& summary': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(2, 0),
      cursor: 'pointer',
      listStyle: 'none',
      '&::-webkit-details-marker': { display: 'none' },
    },
    '& summary::after': {
      content: '"▸"',
      fontSize: '20px',
      color: theme.palette.text.secondary,
      transition: 'transform 0.2s ease',
      flexShrink: 0,
      marginLeft: theme.spacing(1),
    },
    '&[open] summary::after': {
      content: '"▾"',
    },
  },
  cityRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: 1,
  },
  cityLink: {
    fontSize: '20px',
    fontWeight: 600,
    textDecoration: 'none !important',
    '&:hover': {
      textDecoration: 'underline !important',
      color: theme.palette.primary.main,
    },
    fontFamily: 'Cera Round Pro',
  },
  pharmacyList: {
    margin: theme.spacing(0, 0, 2, 0),
    padding: 0,
    listStyle: 'none',
  },
  pharmacyItem: {
    padding: theme.spacing(1, 0),
    borderBottom: `1px dotted ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
    fontFamily: 'Cera Round Pro',
  },
  pharmacyItemLink: {
    textDecoration: 'none !important',
    color: 'inherit',
    '&:hover': {
      textDecoration: 'underline !important',
      color: theme.palette.primary.main,
    },
  },
}));

type Props = {
  cities: CityStatistics[];
};

const PharmacyList: React.FunctionComponent<Props> = ({ cities }) => {
  const classes = useStyles();

  return (
    <Container className={classes.container} maxWidth="lg">
      <Typography variant="h2" className={classes.title}>
        Verkkoapteekit
      </Typography>

      <Typography variant="body1" className={classes.intro}>
        Tässä on kaikki Treetin kautta toimivat verkkoapteekit Suomessa. Näistä verkkoapteekeista voit ostaa
        turvallisesti ja luotettavasti sekä itsehoito- että reseptilääkkeitä. Jokainen Treetin kautta toimiva apteekki
        on Fimean hyväksymä virallinen verkkoapteekki.
      </Typography>

      {cities.map(city => (
        <details key={city.city} className={classes.cityDetails}>
          <summary>
            <div className={classes.cityRow}>
              <Link to={withTrailingSlash(`/verkkoapteekit/${toSlug(city.city)}`)} className={classes.cityLink}>
                {city.city}
              </Link>
            </div>
          </summary>
          <ul className={classes.pharmacyList}>
            {city.pharmacies.map(p => (
              <li key={p.name} className={classes.pharmacyItem}>
                {/* Pharmacy names go to app.treet.fi; city names above stay on treet.fi. */}
                <a href={pharmacyAppUrl(p)} className={classes.pharmacyItemLink}>
                  {p.name}
                </a>
              </li>
            ))}
          </ul>
        </details>
      ))}
    </Container>
  );
};

export default PharmacyList;
